/**
 * Sentry Configuration
 * Error tracking and performance monitoring setup
 */

import * as Sentry from '@sentry/react';

export const initSentry = () => {
  // Only initialize Sentry in production or if DSN is explicitly provided
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry DSN not found. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',

    // Performance monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Filter out specific errors
    beforeSend(event, hint) {
      // Filter out network errors that are not actionable
      if (event.exception) {
        const error = hint.originalException;
        if (error && error instanceof Error) {
          // Filter out Firebase auth errors that are expected
          if (error.message.includes('auth/')) {
            return null;
          }
          // Filter out development-only errors
          if (
            process.env.NODE_ENV === 'development' &&
            error.message.includes('ResizeObserver')
          ) {
            return null;
          }
        }
      }
      return event;
    },

    // Tag all events with additional context
    initialScope: {
      tags: {
        component: 'app-hogar',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      },
      contexts: {
        app: {
          name: 'Sistema de Costeo para Hogar de Adulto Mayor',
          version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        },
      },
    },
  });

  // Set user context when available
  if (process.env.NODE_ENV === 'production') {
    console.log('Sentry initialized for production environment');
  } else {
    console.log('Sentry initialized for development environment');
  }
};

// Helper function to set user context
export const setSentryUser = (user: {
  id: string;
  email: string;
  displayName?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.displayName,
  });
};

// Helper function to clear user context on logout
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Helper function to capture custom messages
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

// Helper function to capture exceptions
export const captureException = (
  error: Error,
  context?: Record<string, any>
) => {
  if (context) {
    Sentry.withScope(scope => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};
